import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  SelectChangeEvent,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Upload as UploadIcon, Image as ImageIcon, Link as LinkIcon, Visibility as VisibilityIcon, FactCheck as StructuredIcon, ShortText as UnstructuredIcon, Verified as VerifiedIcon, FitnessCenter as FitnessCenterIcon } from '@mui/icons-material';
import {
  WorkoutResponseDto,
  GetWorkoutsParams,
  WorkoutCategory,
  WorkoutSourceType,
  MergeWorkoutDto,
  MeasurementUnit,
  WorkoutSortBy,
  SortOrder,
} from '@services/workout.service.ts';
import { adminWorkoutService, WorkoutOptionsResponseDto, ImportWorkoutsResponseDto, ImportSessionDto, WorkoutDetailDto } from '@services/admin-workout.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';
import { toastService } from '@services/toast.service.ts';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { ImportDialog } from '@components/admin/lib/ImportDialog.tsx';
import { MergeDialog } from '@components/admin/lib/MergeDialog.tsx';
import { WorkoutDialog } from '@components/admin/lib/WorkoutDialog.tsx';
import { WorkoutDetailDialog } from '@components/admin/lib/WorkoutDetailDialog.tsx';
import { LibraryFilterPanel } from '@components/admin/lib/LibraryFilterPanel.tsx';
import { LibraryTable, Column } from '@components/admin/lib/LibraryTable.tsx';
import { TranslationStatus } from '@components/common/TranslationStatus.tsx';
import { getImageUrl } from '@utils/image-url.util.ts';

const STORAGE_KEY = 'admin_workout_filters';

export const AdminWorkoutsTab: React.FC = () => {
  // Initialize state from localStorage
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  };

  const savedState = getInitialState();

  const [workouts, setWorkouts] = useState<WorkoutResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(savedState?.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [categoryFilter, setCategoryFilter] = useState<string>(savedState?.categoryFilter || '');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<WorkoutSourceType | ''>(savedState?.sourceTypeFilter || 'all');
  const [expertFilter, setExpertFilter] = useState<number | ''>(savedState?.expertFilter || '');
  const [structuredFilter, setStructuredFilter] = useState<string>(savedState?.structuredFilter || 'all');
  
  // Sorting state
  const [sortBy, setSortBy] = useState<WorkoutSortBy>(savedState?.sortBy || 'name');
  const [sortOrder, setSortOrder] = useState<SortOrder>(savedState?.sortOrder || 'ASC');


  // Modals state
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [workoutToEdit, setWorkoutToEdit] = useState<WorkoutResponseDto | null>(null);
  const [editData, setEditData] = useState<any>(null);

  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState<WorkoutDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState<WorkoutResponseDto | null>(null);

  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [workoutToMerge, setWorkoutToMerge] = useState<WorkoutResponseDto | null>(null);
  const [systemWorkouts, setSystemWorkouts] = useState<WorkoutResponseDto[]>([]);
  const [systemWorkoutsLoading, setSystemWorkoutsLoading] = useState(false);

  // Import state
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportWorkoutsResponseDto | null>(null);
  const [importHistory, setImportHistory] = useState<ImportSessionDto[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isRevertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [sessionToRevert, setSessionToRevert] = useState<ImportSessionDto | null>(null);

  // Options state
  const [options, setOptions] = useState<WorkoutOptionsResponseDto>({ categories: [], measurementUnits: [], experts: [], sourceTypes: [] });
  // const [optionsLoading, setOptionsLoading] = useState(false);

  // Save filters to localStorage
  useEffect(() => {
    const stateToSave = {
      page,
      searchQuery,
      categoryFilter,
      sourceTypeFilter,
      expertFilter,
      structuredFilter,
      sortBy,
      sortOrder
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [page, searchQuery, categoryFilter, sourceTypeFilter, expertFilter, structuredFilter, sortBy, sortOrder]);

  // Fetch options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      // setOptionsLoading(true);
      try {
        const data = await adminWorkoutService.getWorkoutOptions();
        setOptions(data);
      } catch (err) {
        console.error('Failed to fetch workout options:', err);
        toastService.error('Не вдалося завантажити налаштування фільтрів.');
      } finally {
        // setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const fetchWorkouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetWorkoutsParams = {
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sortBy,
        sortOrder,
        category: categoryFilter ? (categoryFilter as WorkoutCategory) : undefined,
        sourceType: sourceTypeFilter || undefined,
        expertId: expertFilter !== '' ? (expertFilter as number) : undefined,
        isStructured: structuredFilter === 'all' ? undefined : structuredFilter === 'true',
      };
      const response = await adminWorkoutService.getWorkouts(params);
      setWorkouts(response.data);
      if (response.meta && response.meta.total && response.meta.limit) {
        setTotalPages(Math.ceil(response.meta.total / response.meta.limit));
      } else {
        setTotalPages(0);
      }
    } catch (err) {
      toastService.error('Не вдалося завантажити список тренувань.');
      setError('Не вдалося завантажити список тренувань.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, categoryFilter, sourceTypeFilter, expertFilter, structuredFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  const fetchSystemWorkouts = useCallback(async (search: string) => {
    setSystemWorkoutsLoading(true);
    try {
      const params: GetWorkoutsParams = {
        limit: 20,
        search: search || undefined,
        sourceType: 'system',
      };
      const response = await adminWorkoutService.getWorkouts(params);
      setSystemWorkouts(response.data);
    } catch (err) {
      toastService.error('Не вдалося завантажити системні тренування.');
    } finally {
      setSystemWorkoutsLoading(false);
    }
  }, []);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };
  const handleCategoryFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setCategoryFilter(event.target.value);
    setPage(1);
  }, []);
  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setSourceTypeFilter(event.target.value as WorkoutSourceType | '');
    setPage(1);
  }, []);
  const handleExpertFilterChange = useCallback((event: SelectChangeEvent<number | ''>) => {
    setExpertFilter(event.target.value as number | '');
    setPage(1);
  }, []);
  const handleStructuredFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setStructuredFilter(event.target.value);
    setPage(1);
  }, []);

  const handleResetFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setSourceTypeFilter('all');
    setExpertFilter('');
    setStructuredFilter('all');
    setPage(1);
    setSortBy('name');
    setSortOrder('ASC');
  };

  const handleSort = (property: string) => {
    const isAsc = sortBy === property && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property as WorkoutSortBy);
  };

  const handleCreateWorkout = async (data: any) => {
    setLoading(true);
    try {
      await adminWorkoutService.createWorkout({
        nameI18n: data.nameI18n,
        category: data.category as WorkoutCategory,
        measurementUnit: data.measurementUnit as MeasurementUnit,
        metValue: data.metValue,
        metActivityId: data.metActivityId,
        defaultMeasurementValue: data.defaultMeasurementValue,
        externalLink: data.externalLink,
        imageId: data.imageId,
        detailsI18n: data.detailsI18n,
      });
      toastService.success('Тренування успішно створено!');
      setCreateModalOpen(false);
      fetchWorkouts();
    } catch (err: any) {
      if (err.data && err.data.errors && Array.isArray(err.data.errors)) {
        const errorMessages = err.data.errors.map((e: any) => e.message).join('\n');
        toastService.error(`Помилка створення:\n${errorMessages}`);
      } else {
        toastService.error(`Помилка створення тренування: ${err.message || 'Невідома помилка'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (workout: WorkoutResponseDto) => {
    setLoading(true);
    setWorkoutToEdit(workout);
    try {
      const data = await adminWorkoutService.getWorkoutForEdit(workout.id);
      setEditData(data);
      setEditModalOpen(true);
    } catch (err) {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetailModal = async (workout: WorkoutResponseDto) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const data = await adminWorkoutService.getWorkoutDetail(workout.id);
      setDetailData(data);
    } catch (err) {
      toastService.error('Не вдалося завантажити деталі тренування.');
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditFromDetail = () => {
    if (detailData) {
      setDetailModalOpen(false);
      handleOpenEditModal({ id: detailData.id } as WorkoutResponseDto);
    }
  };

  const handleUpdateWorkout = async (data: any) => {
    if (!workoutToEdit) return;
    setLoading(true);
    try {
      await adminWorkoutService.updateWorkout(workoutToEdit.id, {
        nameI18n: data.nameI18n,
        category: data.category as WorkoutCategory,
        measurementUnit: data.measurementUnit as MeasurementUnit,
        metValue: data.metValue,
        metActivityId: data.metActivityId,
        defaultMeasurementValue: data.defaultMeasurementValue,
        externalLink: data.externalLink,
        imageId: data.imageId,
        detailsI18n: data.detailsI18n, // Pass detailsI18n
      });
      toastService.success('Тренування успішно оновлено!');
      setEditModalOpen(false);
      setWorkoutToEdit(null);
      fetchWorkouts();
    } catch (err: any) {
       if (err.data && err.data.errors && Array.isArray(err.data.errors)) {
        const errorMessages = err.data.errors.map((e: any) => e.message).join('\n');
        toastService.error(`Помилка оновлення:\n${errorMessages}`);
      } else {
        toastService.error(`Помилка оновлення тренування: ${err.message || 'Невідома помилка'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (workout: WorkoutResponseDto) => {
    setWorkoutToDelete(workout);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteWorkout = async () => {
    if (!workoutToDelete) return;
    setLoading(true);
    try {
      await adminWorkoutService.deleteWorkout(workoutToDelete.id);
      toastService.success(`Тренування "${workoutToDelete.name}" успішно видалено.`);
      setConfirmDeleteOpen(false);
      setWorkoutToDelete(null);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка видалення тренування: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMergeModal = (workout: WorkoutResponseDto) => {
    setWorkoutToMerge(workout);
    setSystemWorkouts([]);
    setMergeModalOpen(true);
  };

  const handleMergeWorkout = async (targetWorkout: WorkoutResponseDto) => {
    if (!workoutToMerge) return;
    setLoading(true);
    try {
      const mergeDto: MergeWorkoutDto = {
        sourceWorkoutId: workoutToMerge.id,
        targetWorkoutId: targetWorkout.id,
      };
      const result = await adminWorkoutService.mergeWorkouts(mergeDto);
      toastService.success(`Тренування '${result.removedWorkout.name}' об'єднано з '${result.targetWorkout.name}'. Оновлено подій: ${result.updatedCalendarEvents}.`);
      setMergeModalOpen(false);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка злиття: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  // Import handlers
  const handleOpenImportModal = () => {
    setImportResult(null);
    setImportModalOpen(true);
  };

  const fetchImportHistory = async () => {
    setHistoryLoading(true);
    try {
      const history = await adminWorkoutService.getImportHistory();
      // Filter history for workouts
      setImportHistory(history.filter(h => h.type === 'WORKOUT'));
    } catch (err) {
      console.error('Помилка завантаження історії:', err);
      toastService.error('Не вдалося завантажити історію імпортів.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await adminWorkoutService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'workouts_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Помилка завантаження шаблону:', err);
      toastService.error('Не вдалося завантажити шаблон.');
    }
  };

  const handleImportWorkouts = async (file: File) => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await adminWorkoutService.importWorkouts(file);
      setImportResult(result);
      if (result.count > 0) {
        toastService.success(`Успішно імпортовано ${result.count} тренувань. Сесія #${result.sessionId}`);
        fetchWorkouts(); // Refresh list
      }
      if (result.errors.length > 0) {
        toastService.warning(`Імпорт завершено з ${result.errors.length} помилками.`);
      }
    } catch (err: any) {
      console.error('Помилка імпорту:', err);
      toastService.error(`Помилка імпорту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenRevertConfirm = (session: ImportSessionDto) => {
    setSessionToRevert(session);
    setRevertConfirmOpen(true);
  };

  const handleRevertImport = async () => {
    if (!sessionToRevert) return;
    
    setHistoryLoading(true);
    try {
      await adminWorkoutService.revertImport(sessionToRevert.id);
      toastService.success(`Імпорт #${sessionToRevert.id} успішно скасовано.`);
      setRevertConfirmOpen(false);
      setSessionToRevert(null);
      fetchImportHistory(); // Refresh history
      fetchWorkouts(); // Refresh list
    } catch (err: any) {
      console.error('Помилка відкату:', err);
      toastService.error(`Помилка відкату: ${err.message || 'Невідома помилка'}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Helper to get label for category
  const getCategoryLabel = (value: string) => {
    const option = options.categories.find(o => o.value === value);
    return option ? option.label : value;
  };

  // Helper to get label for measurement unit
  const getMeasurementUnitLabel = (value: string) => {
    const option = options.measurementUnits.find(o => o.value === value);
    return option ? option.label : value;
  };

  const columns: Column<WorkoutResponseDto>[] = [
    { 
      id: 'name', 
      label: 'Назва', 
      sortable: true,
      render: (workout) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar 
              variant="rounded" 
              src={workout.imageUrl ? getImageUrl(workout.imageUrl) : undefined} 
              alt={workout.name}
              sx={{ width: 40, height: 40 }}
            >
              <ImageIcon />
            </Avatar>
            {workout.legacyImg && (
              <Tooltip title="Є legacy зображення">
                <Box 
                  component="img"
                  src={workout.legacyImg}
                  sx={{ 
                    position: 'absolute', 
                    bottom: -5, 
                    right: -5, 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    border: '1px solid white',
                    zIndex: 1,
                    cursor: 'pointer',
                    objectFit: 'cover',
                    bgcolor: 'background.paper'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(workout.legacyImg!, '_blank');
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">{workout.name}</Typography>
              {workout.isSystem && (
                <Tooltip title="Системний елемент">
                  <VerifiedIcon color="primary" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ mt: 0.5 }}>
              <TranslationStatus availableTranslations={workout.availableTranslations} />
            </Box>
          </Box>
        </Box>
      )
    },
    { id: 'category', label: 'Категорія', sortable: true, render: (workout) => getCategoryLabel(workout.category) },
    // { id: 'measurementUnit', label: 'Одиниця виміру', render: (workout) => getMeasurementUnitLabel(workout.measurementUnit) },
    { id: 'metValue', label: 'MET', render: (workout: any) => workout.metValue ?? '-' },
    { 
      id: 'info', 
      label: 'Інфо', 
      align: 'center',
      render: (workout) => (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          <Tooltip title={workout.isStructured ? "Структурований" : "Неструктурований"}>
            {workout.isStructured ? <StructuredIcon color="success" fontSize="small" /> : <UnstructuredIcon color="disabled" fontSize="small" />}
          </Tooltip>
          
          <Tooltip title={`Використано ${workout.usageCount} разів`}>
            <Chip 
              icon={<VisibilityIcon fontSize="small" />} 
              label={workout.usageCount} 
              size="small" 
              variant="outlined" 
            />
          </Tooltip>

          <Tooltip title={workout.externalLink || "Посилання відсутнє"}>
            <span>
              <IconButton 
                href={workout.externalLink || undefined} 
                target="_blank" 
                size="small" 
                color={workout.externalLink ? "primary" : "default"}
                disabled={!workout.externalLink}
                component={workout.externalLink ? "a" : "button"}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )
    },
    { 
      id: 'createdByUserId', 
      label: 'Джерело', 
      render: (workout) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            {workout.createdByUserId ? 'Користувач' : 'Система'}
          </Typography>
          {workout.legacyId && (
            <Tooltip title={`Імпортовано з легасі тренувань (ID: ${workout.legacyId})`}>
              <FitnessCenterIcon color="action" fontSize="small" />
            </Tooltip>
          )}
        </Stack>
      ) 
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleOpenImportModal} sx={{ mr: 2 }}>Імпорт (Excel)</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)}>Додати тренування</Button>
        </Box>
      </Box>

      <LibraryFilterPanel
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        mainFilterValue={categoryFilter}
        onMainFilterChange={handleCategoryFilterChange}
        mainFilterOptions={options.categories}
        mainFilterLabel="Категорія"
        sourceFilterValue={sourceTypeFilter}
        onSourceFilterChange={handleSourceTypeFilterChange}
        sourceFilterOptions={options.sourceTypes}
        expertFilterValue={expertFilter}
        onExpertFilterChange={handleExpertFilterChange}
        expertOptions={options.experts}
        structuredFilterValue={structuredFilter}
        onStructuredFilterChange={handleStructuredFilterChange}
        onResetFilters={handleResetFilters}
      />

      <LibraryTable
        data={workouts}
        columns={columns}
        loading={loading}
        error={error}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        limit={limit}
        onLimitChange={handleLimitChange}
        storageKey="pagination:admin-workouts"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onEdit={handleOpenEditModal}
        onDelete={handleOpenDeleteConfirm}
        onMerge={handleOpenMergeModal}
        onRowClick={handleOpenDetailModal}
        isUserCreated={(workout) => !!workout.createdByUserId}
      />

      <WorkoutDialog
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateWorkout}
        options={options}
        isLoading={loading}
        title="Додати нове тренування"
        submitLabel="Створити"
      />

      <WorkoutDialog
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateWorkout}
        initialData={editData}
        options={options}
        isLoading={loading}
        title="Редагувати тренування"
        submitLabel="Зберегти зміни"
      />

      <WorkoutDetailDialog
        open={isDetailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onEdit={handleEditFromDetail}
        data={detailData}
        isLoading={detailLoading}
        getCategoryLabel={getCategoryLabel}
        getMeasurementUnitLabel={getMeasurementUnitLabel}
      />

      <ConfirmationDialog 
        open={isConfirmDeleteOpen} 
        onClose={() => setConfirmDeleteOpen(false)} 
        onConfirm={handleDeleteWorkout} 
        title="Підтвердити видалення" 
        message={`Ви впевнені, що хочете видалити тренування "${workoutToDelete?.name}"?`} 
        confirmText="Видалити" 
      />

      <MergeDialog
        open={isMergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        onMerge={handleMergeWorkout}
        sourceItem={workoutToMerge}
        searchOptions={systemWorkouts}
        onSearch={fetchSystemWorkouts}
        isLoading={loading}
        isSearchLoading={systemWorkoutsLoading}
        getOptionLabel={(option) => `${option.name} (${getCategoryLabel(option.category)})`}
        entityName="тренування"
      />

      <ImportDialog
        open={isImportModalOpen}
        onClose={() => setImportModalOpen(false)}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={handleImportWorkouts}
        onFetchHistory={fetchImportHistory}
        onRevert={handleOpenRevertConfirm}
        history={importHistory}
        importResult={importResult}
        isLoading={importLoading}
        isHistoryLoading={historyLoading}
        entityName="тренувань"
      />

      <ConfirmationDialog 
        open={isRevertConfirmOpen} 
        onClose={() => setRevertConfirmOpen(false)} 
        onConfirm={handleRevertImport} 
        title="Скасувати імпорт?" 
        message={`Ви впевнені, що хочете скасувати імпорт #${sessionToRevert?.id}? Всі ${sessionToRevert?.importedCount} тренувань, створених під час цього імпорту, будуть безповоротно видалені.`} 
        confirmText="Скасувати імпорт" 
      />
    </Box>
  );
};
